import type {
  OnlineAccountAccess,
  OnlineAccountCategory,
  OnlineAccountShareAccess,
  OnlineAccountStatus,
  OnlineLoginRole,
  Role,
  Tables,
} from '@/types/database';
import type { SeatProfile } from '@/features/software/model';

export type OnlineAccount = Tables<'online_accounts'>;
export type OnlineLogin = Tables<'online_account_logins'>;
export type OnlineShare = Tables<'online_account_shares'>;

export const ONLINE_ACCOUNT_CATEGORIES: OnlineAccountCategory[] = [
  'software',
  'ecommerce',
  'social',
  'marketing',
  'finance',
  'government',
  'shipping',
  'suppliers',
  'web',
  'utilities',
  'insurance',
  'other',
];

export const ONLINE_ACCOUNT_CATEGORY_LABELS: Record<OnlineAccountCategory, string> = {
  software: 'Software & apps',
  ecommerce: 'Sales & e-commerce',
  social: 'Social media',
  marketing: 'Marketing & ads',
  finance: 'Banking & finance',
  government: 'Government & tax',
  shipping: 'Shipping & freight',
  suppliers: 'Suppliers & wholesale',
  web: 'Website & domains',
  utilities: 'Utilities & phone',
  insurance: 'Insurance',
  other: 'Other',
};

export const ONLINE_ACCOUNT_STATUSES: OnlineAccountStatus[] = ['active', 'closed'];

export const ONLINE_ACCOUNT_STATUS_LABELS: Record<OnlineAccountStatus, string> = {
  active: 'Active',
  closed: 'Closed',
};

export const LOGIN_ROLE_LABELS: Record<OnlineLoginRole, string> = {
  admin: 'Admin',
  user: 'User',
};

export const SHARE_LEVELS: OnlineAccountShareAccess[] = ['view', 'edit', 'manage'];

export const SHARE_LEVEL_LABELS: Record<OnlineAccountShareAccess, string> = {
  view: 'Can view',
  edit: 'Can edit',
  manage: 'Full access',
};

export const SHARE_LEVEL_DESCRIPTIONS: Record<OnlineAccountShareAccess, string> = {
  view: 'See the account and its logins, and view or copy passwords.',
  edit: 'Also change details, add or remove logins, and change passwords.',
  manage: 'Also share the account with other people.',
};

export const ACCESS_LABELS: Record<OnlineAccountAccess, string> = {
  owner: 'Super admin',
  manage: 'Full access',
  edit: 'Can edit',
  view: 'Can view',
  login: 'Specific logins',
};

export function canEditAccount(access: OnlineAccountAccess | null | undefined): boolean {
  return access === 'owner' || access === 'manage' || access === 'edit';
}

export function canShareAccount(access: OnlineAccountAccess | null | undefined): boolean {
  return access === 'owner' || access === 'manage';
}

/** A timestamp's calendar day in the viewer's time zone, as `YYYY-MM-DD`. */
export function localIsoDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function isShareActive(share: Pick<OnlineShare, 'expires_at'>, now = Date.now()): boolean {
  return !share.expires_at || new Date(share.expires_at).getTime() > now;
}

/**
 * The signed-in person's access to one account. Mirrors
 * public.online_account_access() so the UI shows the right buttons; the
 * database still decides.
 */
export function accountAccessFor(
  accountId: string,
  role: Role | null,
  myShares: OnlineShare[],
  now = Date.now(),
): OnlineAccountAccess | null {
  if (!role || role === 'external_lender') return null;
  if (role === 'super_admin') return 'owner';
  const mine = myShares.filter((s) => s.account_id === accountId && isShareActive(s, now));
  const whole = mine.find((s) => s.login_id === null);
  if (whole) return whole.access;
  return mine.length > 0 ? 'login' : null;
}

/** Access to one login: the account level, or that login's own share. */
export function loginAccessFor(
  login: Pick<OnlineLogin, 'id'>,
  accountAccess: OnlineAccountAccess | null,
  myShares: OnlineShare[],
  now = Date.now(),
): OnlineAccountAccess | null {
  if (accountAccess !== 'login') return accountAccess;
  const share = myShares.find((s) => s.login_id === login.id && isShareActive(s, now));
  return share?.access ?? null;
}

export function loginLabel(login: Pick<OnlineLogin, 'username' | 'email'>): string {
  return login.username || login.email || 'Login';
}

export function loginHolderName(
  login: Pick<OnlineLogin, 'profile_id' | 'holder_name'>,
  profilesById: Map<string, SeatProfile>,
): string | null {
  const profile = login.profile_id ? profilesById.get(login.profile_id) : undefined;
  return profile?.full_name || profile?.email || login.holder_name || null;
}

export function profileName(
  id: string | null | undefined,
  profilesById: Map<string, SeatProfile>,
): string | null {
  if (!id) return null;
  const profile = profilesById.get(id);
  return profile ? profile.full_name || profile.email : null;
}

/** One person's access to one account, collapsed from their share rows. */
export interface PersonAccess {
  profileId: string;
  access: OnlineAccountShareAccess;
  /** Empty for a whole-account share. */
  loginIds: string[];
  expiresAt: string | null;
  grantedBy: string | null;
  updatedAt: string;
  expired: boolean;
  /** Deactivated or now a lender. The database ignores their shares until this changes. */
  inactive: boolean;
}

/** Whether the share actually lets the person in right now. */
export function hasLiveAccess(person: Pick<PersonAccess, 'expired' | 'inactive'>): boolean {
  return !person.expired && !person.inactive;
}

export function groupShares(
  shares: OnlineShare[],
  inactiveIds: ReadonlySet<string>,
  now = Date.now(),
): PersonAccess[] {
  const byPerson = new Map<string, OnlineShare[]>();
  for (const share of shares) {
    const list = byPerson.get(share.profile_id);
    if (list) list.push(share);
    else byPerson.set(share.profile_id, [share]);
  }
  return [...byPerson.entries()].map(([profileId, rows]) => {
    const whole = rows.find((r) => r.login_id === null);
    const first = whole ?? rows[0];
    return {
      profileId,
      access: first.access,
      loginIds: whole ? [] : rows.map((r) => r.login_id).filter((v): v is string => !!v),
      expiresAt: first.expires_at,
      grantedBy: first.granted_by,
      updatedAt: rows.reduce((max, r) => (r.updated_at > max ? r.updated_at : max), first.updated_at),
      expired: !isShareActive(first, now),
      inactive: inactiveIds.has(profileId),
    };
  });
}

/** e.g. "Can edit · whole account" or "Can view · 2 logins". */
export function describeAccess(
  person: Pick<PersonAccess, 'access' | 'loginIds'>,
  row: { logins: OnlineLogin[] },
): string {
  if (person.loginIds.length === 0) return `${SHARE_LEVEL_LABELS[person.access]} · whole account`;
  const names = person.loginIds
    .map((id) => row.logins.find((l) => l.id === id))
    .filter((l): l is OnlineLogin => !!l)
    .map((l) => loginLabel(l));
  const level = person.access === 'edit' ? 'Can change password' : 'Can view';
  return `${level} · ${names.length === 1 ? names[0] : `${person.loginIds.length} logins`}`;
}

export interface OnlineAccountRow extends OnlineAccount {
  /** Admin logins first, then users, oldest first within each. */
  logins: OnlineLogin[];
  adminLogin: OnlineLogin | null;
  myAccess: OnlineAccountAccess | null;
  /** Everyone's access. Only filled in for people who can share the account. */
  people: PersonAccess[];
  openedByName: string | null;
}

export function sortLogins(logins: OnlineLogin[]): OnlineLogin[] {
  return [...logins].sort((a, b) => {
    if (a.role !== b.role) return a.role === 'admin' ? -1 : 1;
    return a.created_at.localeCompare(b.created_at);
  });
}

export function buildOnlineAccountRows(
  accounts: OnlineAccount[],
  logins: OnlineLogin[],
  shares: OnlineShare[],
  myId: string | null,
  role: Role | null,
  profilesById: Map<string, SeatProfile>,
  inactiveIds: ReadonlySet<string>,
): OnlineAccountRow[] {
  const now = Date.now();
  const loginsByAccount = new Map<string, OnlineLogin[]>();
  for (const login of logins) {
    const list = loginsByAccount.get(login.account_id);
    if (list) list.push(login);
    else loginsByAccount.set(login.account_id, [login]);
  }
  const sharesByAccount = new Map<string, OnlineShare[]>();
  for (const share of shares) {
    const list = sharesByAccount.get(share.account_id);
    if (list) list.push(share);
    else sharesByAccount.set(share.account_id, [share]);
  }
  const myShares = myId ? shares.filter((s) => s.profile_id === myId) : [];

  return accounts.map((account) => {
    const accountLogins = sortLogins(loginsByAccount.get(account.id) ?? []);
    const myAccess = accountAccessFor(account.id, role, myShares, now);
    return {
      ...account,
      logins: accountLogins,
      adminLogin: accountLogins.find((l) => l.role === 'admin') ?? null,
      myAccess,
      people: canShareAccount(myAccess)
        ? groupShares(sharesByAccount.get(account.id) ?? [], inactiveIds, now)
        : [],
      openedByName: profileName(account.opened_by, profilesById) ?? account.opened_by_name,
    };
  });
}

const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGITS = '23456789';
const SYMBOLS = '!@#$%^&*-_=+?';

function randomIndex(max: number): number {
  const limit = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);
  do crypto.getRandomValues(buf);
  while (buf[0] >= limit);
  return buf[0] % max;
}

/** A random password with at least one of each character class. Look-alikes (l, 1, O, 0) are left out. */
export function generatePassword(length = 20): string {
  const sets = [LOWER, UPPER, DIGITS, SYMBOLS];
  const all = sets.join('');
  const chars = sets.map((set) => set[randomIndex(set.length)]);
  while (chars.length < length) chars.push(all[randomIndex(all.length)]);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

export function friendlyOnlineAccountError(err: unknown, fallback: string): string {
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === 'object' && err && 'message' in err
        ? String((err as { message: unknown }).message)
        : '';
  if (/online_accounts_name_uniq/i.test(msg)) {
    return 'An account with that name is already on the list.';
  }
  if (/online_accounts_name_len/i.test(msg)) return 'Give the account a name.';
  if (/online_account_logins_identity/i.test(msg)) {
    return 'Enter a username or an email for the login.';
  }
  if (/online_account_shares_uniq/i.test(msg)) return 'That person already has access.';
  if (/row-level security|permission denied/i.test(msg)) {
    return 'You don’t have permission to change this account.';
  }
  return msg || fallback;
}
